import { colors } from "./colors";

/**
 * Shared UI tokens. Names describe a role, not a literal value, so the whole
 * app can be re-spaced or re-shadowed from one place. Colours stay in
 * `colors.ts`, which is driven by the bank's palette environment variable.
 */

/** Spacing scale, in px. */
export const space = {
  xs: 4,
  sm: 8,
  md: 12,
  lg: 16,
  xl: 20,
  "2xl": 24,
  "3xl": 32,
} as const;

/* The football app's radius scale: a 10px base for controls, 14px for a
   card and 20px for a panel. Rounder than the 8px it replaced, which is
   most of why the two products still looked unrelated at a glance even
   once the greys matched. */
export const radii = {
  sm: 5,
  /* Football picks a corner from an element's height rather than its kind,
     and everything from a 27px in-list action to a 30px in-card control sits
     on 8px — which is why this is its own step and not `md` rounded down. */
  control: 8,
  md: 10,
  /* A dismissible surface sits a step rounder than a card: 12px for a menu
     panel, 18px for a modal. */
  menu: 12,
  lg: 14,
  modal: 18,
  xl: 20,
  pill: 999,
} as const;

/** Shadows are tinted with the neutral hue rather than pure black. */
export const shadow = {
  /* A card in the football app has no shadow at all — it is a white plane
     inside a ring, and the ring alone separates it from the canvas. What is
     left here is for things that genuinely sit above the page, plus the
     one small lift that marks the moving thumb of a segmented control. */
  thumb: "0 1px 2px rgba(15, 20, 25, 0.06)",
  card: "none",
  raised: "0 2px 8px rgba(15, 20, 25, 0.05)",
  overlay: "0 12px 32px -12px rgba(15, 20, 25, 0.18)",
  /* Football's own `--shadow-pop`, for the surfaces it allows a shadow: a
     menu, a modal, a popover. In that system a shadow is not decoration, it
     means "this can be dismissed", which is why a card has none. */
  pop: "0 32px 80px -20px rgba(15, 20, 25, 0.26)",
  focusRing: `0 0 0 4px ${colors.primary[15]}`,
} as const;

/** The scrim behind a modal: the page is dimmed, never frosted. */
export const scrim = "rgba(15, 20, 25, 0.28)";

export const line = {
  /* The football app draws a card's edge and a row's divider in two
     different weights: the ring around a card is the heavier of the two,
     and the rules inside it are light enough to read as spacing. */
  hairline: colors.gray[200],
  soft: colors.gray[100],
  /* One step in from the hairline, and the difference is meant: a form field
     that collects a value takes this edge, while a page control that filters
     what is already on screen takes the card's — so a toolbar reads as chrome
     and a form reads as work. */
  field: "#E4E7EC",
  dashed: colors.gray[300],
} as const;

/** Role-named text styles, interpolated into styled-components. */
export const text = {
  /* Quiet and small. A card's title is how you find the card, not what you
     read once you have found it, and holding it back gives the amount below
     it room to be the loudest thing in the card. Small enough that the
     longer titles stay on one line in a narrow tile. */
  cardTitle: `
    font-size: 13px;
    font-weight: 600;
    line-height: 1.3;
    letter-spacing: -0.005em;
    color: ${colors.gray[700]};
  `,
  /* A card's headline figure. 600 rather than 700: football sets its KPI
     value semibold, and at this size the extra weight stopped reading as
     emphasis and started reading as a different, heavier typeface than
     everything around it. */
  hero: `
    font-weight: 600;
    font-size: 24px;
    line-height: 1.15;
    letter-spacing: -0.022em;
    color: ${colors.gray[900]};
  `,
  /* Captions under a figure rather than headings above one: small, muted
     and not competing with the number they belong to. */
  label: `
    font-size: 11px;
    font-weight: 500;
    line-height: 15px;
    color: ${colors.gray[500]};
  `,
  value: `
    font-size: 15px;
    font-weight: 600;
    line-height: 20px;
    letter-spacing: -0.01em;
    color: ${colors.gray[900]};
  `,
  legend: `
    font-size: 13px;
    line-height: 20px;
    color: ${colors.gray[500]};
  `,
  /** Align digits in columns and across rows. */
  numeric: "font-variant-numeric: tabular-nums;",
} as const;

/** Semantic tones for gains, losses and neutral values. */
export type Tone = "up" | "down" | "flat";

export const tone: Record<Tone, { bg: string; fg: string; dot: string }> = {
  up: {
    bg: colors.success[50],
    fg: colors.success[700],
    dot: colors.success[500],
  },
  down: { bg: colors.error[50], fg: colors.error[700], dot: colors.error[500] },
  flat: { bg: colors.gray[100], fg: colors.gray[600], dot: colors.gray[400] },
};

/**
 * The colours a SERIES is drawn in — a chart line and the wash under it.
 *
 * Deliberately not the status tones above. Those are tuned for text and
 * chips: a 700 step on a 50 tint, picked for contrast at 12px, and the green
 * of them is the same green a COMPLETED tag wears. A chart asks for
 * something different from the same idea:
 *
 * - It is a 2px stroke over a large, faint wash, not letterforms. The
 *   status green reads heavy at that weight and the status red reads hot
 *   enough to look like an alert rather than a downward month.
 * - The green is shifted towards teal. Market charting has settled on this
 *   for a reason worth keeping: a pure green against a pure red is the
 *   hardest pair in the spectrum for the commonest colour deficiency, and
 *   moving the green towards blue separates the two by lightness and hue
 *   both. It also keeps the chart's green from being read as the "success"
 *   green in a status tag two inches away, which is a different claim.
 *
 * Grey for a window with no direction: it asserts nothing, where either of
 * the other two would.
 */
export const seriesTone: Record<Tone, string> = {
  up: "#22AB94",
  down: "#F7525F",
  flat: colors.gray[500],
};

/**
 * Turn off Ant Design's number steppers.
 *
 * `controls={false}` does nothing here: the prop arrived in antd 4.17 and
 * this app is on 4.16.13, so every InputNumber in it renders the up/down
 * handler and reveals it on hover — five call sites pass the prop and all
 * five are ignored. The original code hid the handler by absolutely
 * positioning its wrapper and blanking two pseudo-elements, which is the
 * same idea spelled at length and easy to drop by accident. One rule does
 * it, and it says why it exists.
 */
export const noSteppers = `
  .ant-input-number-handler-wrap {
    display: none;
  }
`;

export const toneOf = (value?: number): Tone => {
  if (!value || Number.isNaN(value)) return "flat";
  return value > 0 ? "up" : value < 0 ? "down" : "flat";
};

/**
 * A colour at partial strength.
 *
 * For the one case tints are wanted from a palette that ships only solid
 * steps — a mark's disc under its glyph. Written here rather than as a hex
 * literal at the call site, because the palette is driven by
 * REACT_APP_BANK_COLORS and a tint typed by hand is the wrong brand on any
 * other tenant.
 */
export const withAlpha = (color: string, alpha: number) => {
  const hex = color.trim().replace("#", "");
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;
  if (full.length !== 6) return color;
  const n = parseInt(full, 16);
  /* eslint-disable no-bitwise */
  return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`;
  /* eslint-enable no-bitwise */
};

/** A colour's channels, or null when it is not a hex we can read. */
const channels = (color: string) => {
  const hex = color.trim().replace("#", "");
  const full =
    hex.length === 3
      ? hex
          .split("")
          .map((c) => c + c)
          .join("")
      : hex;
  if (full.length !== 6) return null;
  const n = parseInt(full, 16);
  /* eslint-disable no-bitwise */
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  /* eslint-enable no-bitwise */
};

const hex = (rgb: number[]) =>
  `#${rgb
    .map((c) =>
      Math.max(0, Math.min(255, Math.round(c)))
        .toString(16)
        .padStart(2, "0"),
    )
    .join("")}`;

/**
 * The same colour, lighter or darker.
 *
 * For the one case a palette of solid steps cannot serve: the faces of a
 * drawn solid, which are one colour under three amounts of light. Mixing
 * toward white or black keeps the hue, so the three faces still read as one
 * material — which is what tells a reader they are looking at a shape rather
 * than three shapes.
 */
export const tint = (color: string, amount: number) => {
  const rgb = channels(color);
  return rgb ? hex(rgb.map((c) => c + (255 - c) * amount)) : color;
};

export const shade = (color: string, amount: number) => {
  const rgb = channels(color);
  return rgb ? hex(rgb.map((c) => c * (1 - amount))) : color;
};

/**
 * White, unless white cannot be read on it.
 *
 * For a figure printed INSIDE a coloured mark — a share inside a ring's arc,
 * a count inside a filled chip. White is the right answer on most of this
 * palette and the wrong one on the rest of it: measured against white, the
 * accent clears 3.4:1 and the deep grey 9.5, while the magenta sits at 2.3,
 * the yellow at 1.9 and the light grey at 1.9. A label that is white on every
 * arc is unreadable on a third of them.
 *
 * So the colour is COMPUTED per mark rather than chosen once: white while it
 * clears the bar, and otherwise the mark's own hue taken down until it does,
 * which keeps the label inside the family instead of dropping a black figure
 * onto a coloured field.
 */
const luminance = (color: string) => {
  const rgb = channels(color);
  if (!rgb) return 0;
  const [r, g, b] = rgb.map((c) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
};

/** The WCAG ratio between two colours, 1 (identical) to 21 (black on white). */
export const contrast = (a: string, b: string) => {
  const la = luminance(a);
  const lb = luminance(b);
  const [hi, lo] = la > lb ? [la, lb] : [lb, la];
  return (hi + 0.05) / (lo + 0.05);
};

export const readableOn = (background: string, minimum = 3) =>
  contrast(colors.white, background) >= minimum
    ? colors.white
    : shade(background, 0.55);
